<template>
  <li
    :data-ui="`session-card-${index}`"
    :class="{
      'opo-card--hidden': singleMonth && index >= 8,
      'opo-card--secondary-theme': isSecondaryTheme,
    }"
    class="opo-card"
  >
    <div class="opo-card__details">
      <cdr-text
        class="opo-card__week-day"
        data-ui="session-week-day"
      >
        {{ displayDayName }}
      </cdr-text>
      <div
        :class="`opo-card__date--${cardType}`"
        data-ui="session-display-date"
      >
        {{ displayDate }}
      </div>
      <div
        class="opo-card__time"
        data-ui="session-display-time"
      >
        {{ displayTime }}
      </div>
    </div>
    <div class="opo-card__details">
      <div class="opo-card__price">
        <cdr-text
          v-if="priceShown"
          class="opo-card__details--compact"
          data-ui="session-price"
        >
          {{ memberPrice }}
        </cdr-text>
      </div>
      <div class="opo-card__button">
        <cdr-button
          :href="session.uri"
          :modifier="`${isSecondaryTheme ? 'secondary' : 'primary'}`"
          tag="a"
          class="opo-card__button-link"
          data-ui="registration-button"
          size="small medium@md medium@lg"
          rel="nofollow"
          @click="handleMetrics"
        >
          {{ registrationButtonText }}
        </cdr-button>
      </div>
      <div class="opo-card__status">
        <cdr-text
          v-if="registrationMessage"
          :class="statusClass"
          class="opo-card__details--compact"
          data-ui="registration-status-message"
        >
          {{ registrationMessage }}
        </cdr-text>
      </div>
    </div>
    <hr class="opo-card__line-break" />
    <div class="opo-card__location">
      <icon-location-pin-fill
        class="opo-card__location-icon"
        size="small"
      />
      <cdr-text
        class="opo-card__details--compact"
        data-ui="session-location"
      >
        {{ session.location.name }}
      </cdr-text>
    </div>
  </li>
</template>

<script>
import { CdrButton, CdrText, IconLocationPinFill } from '@rei/cedar';
import moment from 'moment';
import 'moment-timezone';
import metrics from '../mixins/metrics';
import fixTimeZone from '../common/timeZone';

export default {
  name: 'CardsComponent',
  components: {
    CdrButton,
    CdrText,
    IconLocationPinFill,
  },
  mixins: [metrics],
  props: {
    session: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: false,
      default: null,
    },
    singleMonth: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      canceledMessage: 'Canceled',
      closedMessage: 'Registration closed',
      soldOutMessage: 'Sold out',
      limitedSpotsMessage: 'Limited spots left',
    };
  },
  computed: {
    /*
     * Parsed and formatted date helpers for computed properties
     */
    // Returns parsed start date
    start() {
      const timezone = this.session.meetingLocation
        ? this.session.meetingLocation.timezone
        : this.session.location.timezone;
      return moment.parseZone(this.session.start).tz(timezone);
    },
    // Returns parsed end date
    end() {
      const timezone = this.session.meetingLocation
        ? this.session.meetingLocation.timezone
        : this.session.location.timezone;
      return moment.parseZone(this.session.end).tz(timezone);
    },
    // Returns the day of the week full name
    dayOfWeek() {
      return this.start.format('dddd');
    },
    // Returns numerical start date day
    startDay() {
      return this.start.format('D');
    },
    // Returns abreviated start date month
    startMonth() {
      return this.start.format('MMM');
    },
    // Returns start date year
    startYear() {
      return this.start.format('YYYY');
    },
    // Returns numerical end date day
    endDay() {
      return this.end.format('D');
    },
    // Returns abreviated end date month
    endMonth() {
      return this.end.format('MMM');
    },
    // Returns end date year
    endYear() {
      return this.end.format('YYYY');
    },
    // Determines the date range type
    dateRangeType() {
      const { start, end } = this;
      // Same day
      if (end.isSame(start, 'day')) {
        return 'singleDay';
      }
      // Same month, diff day
      if (end.isSame(start, 'month')) {
        return 'multiDay';
      }
      // Same year, diff month, diff day
      if (end.isSame(start, 'year')) {
        return 'multiMonth';
      }
      // Diff year, diff month, diff day
      return 'multiYear';
    },
    // Returns date output for single day sessions
    singleDay() {
      return `${this.startMonth} ${this.startDay}, ${this.startYear}`;
    },
    // Returns date output for multi day sessions
    multiDay() {
      return `${this.startMonth} ${this.startDay}-${this.endDay}, ${this.startYear}`;
    },
    // Returns date output for multi month and day sessions
    multiMonth() {
      return `${this.startMonth} ${this.startDay}-${this.endMonth} ${this.endDay}, ${this.startYear}`;
    },
    // Returns date output for multi year sessions
    multiYear() {
      return `${this.startMonth} ${this.startDay}, ${this.startYear}-${this.endMonth} ${this.endDay}, ${this.endYear}`;
    },
    /*
     * Properties that determine displayed values
     */
    // Displays appropriate month, day and year depending on conditions
    displayDate() {
      const type = this.dateRangeType;
      return this[type];
    },
    // Returns the day of the week to be displayed
    displayDayName() {
      return this.dayOfWeek;
    },
    // Returns start to end time to be displayed
    displayTime() {
      const { start, end } = this;

      const timeZoneId = start.format('z');
      const tz = fixTimeZone(timeZoneId);

      return `${start.format('h:mma')}–${end.format('h:mma')} ${tz}`;
    },
    // Returns price display text that should be shown if any
    memberPrice() {
      let price = null;
      let priceText = 'Cost: free!';
      if (this.session.memberPrice === 0) {
        price = priceText;
      }
      if (this.session.memberPrice > 0) {
        priceText = 'from $';
        price = `${priceText}${this.session.memberPrice}`;
      }
      return price;
    },
    /*
     * Session status computed contional helpers
     */
    // Returns session status canceled
    sessionCanceled() {
      return this.session.status === 'CANCELED';
    },
    // Returns session status closed
    sessionClosed() {
      return this.session.status === 'CLOSED';
    },
    // Returns session status third party
    thirdParty() {
      return this.session.spots.registrationLocation === 'THIRD_PARTY';
    },
    // Returns session status wait list
    sessionWaitList() {
      return this.session.spots.status === 'WAIT_LIST';
    },
    // Returns session status sold out (wait list is not available)
    sessionSoldOut() {
      return this.session.spots.status === 'SOLD_OUT';
    },
    // Returns registration required true
    registrationRequired() {
      return this.session.registrationRequired === true;
    },
    // Conditional for use to determine if registration is available for a session
    registrationAvailable() {
      const {
        sessionClosed,
        thirdParty,
        sessionCanceled,
        sessionWaitList,
        sessionSoldOut,
        registrationRequired,
      } = this;
      return (
        !sessionClosed &&
        !thirdParty &&
        !sessionCanceled &&
        !sessionWaitList &&
        !sessionSoldOut &&
        registrationRequired
      );
    },
    // Conditional for use to determine if session is full
    sessionFull() {
      const { sessionWaitList, sessionClosed, sessionCanceled, sessionSoldOut } = this;
      return sessionWaitList && !sessionClosed && !sessionCanceled && !sessionSoldOut;
    },
    // Conditional for use to determine if session is third party registered
    isThirdParty() {
      const { thirdParty, sessionClosed, sessionCanceled } = this;
      return thirdParty && !sessionClosed && !sessionCanceled;
    },
    // Conditional for use to determine if registration is required
    isRegistrationRequired() {
      const { registrationRequired, sessionClosed, sessionCanceled, sessionSoldOut } = this;
      return registrationRequired && !sessionClosed && !sessionCanceled && !sessionSoldOut;
    },
    // Conditional for use to determine if limited spots
    limitedSpotsAvailable() {
      let limitedSpotsAvailable = false;
      const numberOfSpots = this.session.spots.available;
      if (
        this.registrationAvailable &&
        numberOfSpots !== null &&
        numberOfSpots <= 5 &&
        numberOfSpots > 0
      ) {
        limitedSpotsAvailable = true;
      }
      return limitedSpotsAvailable;
    },
    /*
     * Properties that determine displayed values and themes
     */
    // Returns registration message to display depending on session status
    registrationMessage() {
      let registrationMessage = null;
      if (this.registrationAvailable) {
        registrationMessage = `${this.session.spots.available} spots left`;
      }
      // Handles Garage sale sessions that are open for reg. but spots avail return null
      if (this.registrationAvailable && this.session.spots.available === null) {
        registrationMessage = 'Come one, come all!';
      }
      if (this.limitedSpotsAvailable) {
        registrationMessage = this.limitedSpotsMessage;
      }
      if (this.sessionCanceled) {
        registrationMessage = this.canceledMessage;
      }
      if (this.sessionClosed) {
        registrationMessage = this.closedMessage;
      }
      if (this.sessionFull || this.sessionSoldOut) {
        registrationMessage = this.soldOutMessage;
      }
      return registrationMessage;
    },
    // Returns registration button text to display depending on session status
    registrationButtonText() {
      let registrationButton = 'Details & sign up';
      if (this.sessionFull) {
        registrationButton = 'Join the waitlist';
      }
      if (
        this.sessionCanceled ||
        this.sessionClosed ||
        this.isThirdParty ||
        !this.isRegistrationRequired ||
        this.sessionSoldOut
      ) {
        registrationButton = 'See details';
      }
      return registrationButton;
    },
    // Returns the appropriate modified class depending on session status
    statusClass() {
      let statusClass = null;
      if (this.sessionCanceled || this.sessionClosed || this.sessionSoldOut) {
        statusClass = 'opo-card__details--compact--red';
      }
      if (this.sessionFull) {
        statusClass = 'opo-card__details--compact--red';
      }
      if (this.registrationAvailable) {
        statusClass = 'opo-card__details--compact--green';
      }
      if (this.limitedSpotsAvailable) {
        statusClass = 'opo-card__details--compact--orange';
      }
      return statusClass;
    },
    // Determines if is secondary theme depending on session status
    isSecondaryTheme() {
      const { sessionCanceled, sessionClosed, sessionFull, sessionSoldOut } = this;
      return sessionCanceled || sessionClosed || sessionFull || sessionSoldOut;
    },
    // Determines if price should be shown depending on session status
    priceShown() {
      const { sessionCanceled, sessionClosed, sessionFull, isThirdParty, sessionSoldOut } = this;
      return !(sessionCanceled || sessionClosed || sessionFull || isThirdParty || sessionSoldOut);
    },
    cardType() {
      const type = this.dateRangeType;
      return type === 'singleDay' ? 'large' : 'medium';
    },
  },
  methods: {
    handleMetrics() {
      this.metricsClick({ linkName: `{{ pageName }}_session click_${this.session.sessionId}` });
    },
  },
};
</script>
